import styled from '@emotion/styled';
import type {
  BadgeIntent,
  BadgeShape,
  BadgeSize,
  BadgeSubtleVariant,
} from './types';

type StyledBadgeProps = {
  $intent: BadgeIntent;
  $variant: BadgeSubtleVariant;
  $size: BadgeSize;
  $shape: BadgeShape;
  $uppercase: boolean;
};

function resolveColors(
  theme: any,
  intent: BadgeIntent,
  variant: BadgeSubtleVariant
): { backgroundColor: string; color: string; borderColor?: string } {
  const intents = theme?.component?.badge?.intents;

  if (intent === 'subtle') {
    return (
      intents?.subtle?.[variant] ?? {
        backgroundColor: 'rgba(47, 128, 106, 0.1)',
        color: '#2f806a',
      }
    );
  }

  return (
    intents?.[intent] ?? {
      backgroundColor: '#292929',
      color: '#ffffff',
    }
  );
}

export const StyledBadge = styled.span<StyledBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-sizing: border-box;
  white-space: nowrap;
  font-weight: ${({ theme }) => theme?.component?.badge?.fontWeight ?? 700};
  letter-spacing: ${({ theme }) =>
    theme?.component?.badge?.letterSpacing ?? '0.05em'};
  line-height: ${({ theme }) => theme?.component?.badge?.lineHeight ?? 1};
  text-transform: ${({ $uppercase }) => ($uppercase ? 'uppercase' : 'none')};

  font-size: ${({ theme, $size }) =>
    theme?.component?.badge?.sizes?.[$size]?.fontSize ?? '0.75rem'};

  ${({ theme, $intent, $variant, $size, $shape }) => {
    const colors = resolveColors(theme, $intent, $variant);
    const sizeTokens = theme?.component?.badge?.sizes?.[$size];
    const shapeTokens = theme?.component?.badge?.shapes?.[$shape];

    const border =
      colors.borderColor != null
        ? `border: 1px solid ${colors.borderColor};`
        : 'border: none;';

    if ($shape === 'circle') {
      const circle = sizeTokens?.circle ?? {
        minWidth: '16px',
        height: '16px',
        padding: '0 4px',
      };
      return `
        background-color: ${colors.backgroundColor};
        color: ${colors.color};
        ${border}
        border-radius: ${shapeTokens?.borderRadius ?? '50%'};
        min-width: ${circle.minWidth};
        height: ${circle.height};
        padding: ${circle.padding};
      `;
    }

    return `
      background-color: ${colors.backgroundColor};
      color: ${colors.color};
      ${border}
      border-radius: ${shapeTokens?.borderRadius ?? '4px'};
      padding: ${sizeTokens?.padding ?? '4px 10px'};
    `;
  }}

  svg {
    width: ${({ theme, $size }) =>
      theme?.component?.badge?.sizes?.[$size]?.iconSize ?? '14px'};
    height: ${({ theme, $size }) =>
      theme?.component?.badge?.sizes?.[$size]?.iconSize ?? '14px'};
    flex-shrink: 0;
  }

  @media (forced-colors: active) {
    border: 1px solid ButtonText;
  }
`;

export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
`;
